import React, { FC } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { flatten } from '@turf/flatten'
import rewind from '@turf/rewind'
import { useNavigationContext } from '@common/hooks'
import {
  useCreateProjectMapLayer,
  useUpdateProjectMapLayer,
} from '@core/react-query/features/projects/maps'
import { Box, Flex, Text } from '@fundamentals'
import { TextField, Button } from '@common/material'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import { localizedStrings } from '@core/strings'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'
import {
  convertKMLToGeoJSON,
  repairSingleCoordinateLineStringsIssue,
} from '@modules/geolocation/utilities'
import { LayerItem } from './LayerItem'
import { featureCollection } from '@turf/helpers'
import { useProjectMapConfigurator } from '@modules/geolocation'
import { useModal } from '@common/GlobalModal'
import ConfirmModal from '@common/modal/ConfirmModal'

interface LayerFormProps {
  onDismiss: () => void
}

interface LayerFormData {
  id: number | string
  name: string
  visible: boolean
  file: File[]
  fileName: string
  localGeoJson: any
  type: 'KMZ' | 'KML'
}

export const LayerForm: FC<LayerFormProps> = ({ onDismiss }) => {
  const { projectId } = useNavigationContext()
  const { showModal, hideModal } = useModal()
  const {
    drawRef,
    selectedLayer,
    addLayerToMap,
    deleteLayerFromMap,
    toggleLayerVisibilityOnMap,
    zoomToLayerOnMap,
    hasLayerGeoJsonChanges,
    setHasLayerGeoJsonChanges,
  } = useProjectMapConfigurator()
  const createProjectMapLayerMutation = useCreateProjectMapLayer()
  const updateProjectMapLayerMutation = useUpdateProjectMapLayer()

  const { control, watch, handleSubmit, setValue } = useForm<LayerFormData>({
    defaultValues: {
      id: selectedLayer?.id ?? uuidv4(),
      name: selectedLayer?.name ?? '',
      visible: selectedLayer?.visible ?? true,
      file: [],
      type: null,
      // fileName: selectedLayer?.fileName ?? '',
      fileName: selectedLayer?.name ?? '',
      localGeoJson: null,
    },
  })

  const watchLayerId = watch('id')
  const watchVisible = watch('visible')
  const watchFileName = watch('fileName')
  const hasFile = Boolean(watchFileName)
  const watchLocalGeoJson: GeoJSON.FeatureCollection = watch('localGeoJson')

  const onSubmit: SubmitHandler<LayerFormData> = async (data) => {
    const localGeoJsonForServer = featureCollection(
      drawRef.current
        .getAll()
        .features.filter(
          (feature) => feature.properties.layerId === watchLayerId,
        )
        .map((feature) => {
          // Destructure local layerId and visible out of properties and keep the rest
          const { layerId, visible, ...otherProperties } = feature.properties

          return {
            ...feature,
            properties: otherProperties,
          }
        }),
    )

    if (!selectedLayer) {
      const response = await createProjectMapLayerMutation.mutateAsync({
        projectId: projectId,
        name: data.name,
        type: data.type,
        file: data.file?.[0],
        localGeoJson: localGeoJsonForServer,
      })

      // Add server layerId after creation of layer
      addLayerToMap(response.id, localGeoJsonForServer)
    } else {
      // Update the layer on the server
      // No need to add the layer to the map here because it's already in the map
      await updateProjectMapLayerMutation.mutateAsync({
        projectId: projectId,
        layerId: Number(data.id),
        name: data.name,
        visible: data.visible,
        // Only send file if there are changes
        file: hasLayerGeoJsonChanges ? data.file?.[0] : null,
        // Only send localGeoJson if there are changes
        localGeoJson: hasLayerGeoJsonChanges ? localGeoJsonForServer : null,
      })

      setHasLayerGeoJsonChanges(false)
    }

    onDismiss()
  }

  const handleOnAttachmentChange = async (files: File[]) => {
    // Return early if there are no files provided
    if (!files?.length) return

    const file = files[0]

    try {
      // Convert the file (either KMZ or KML) to GeoJSON
      const geoJSON = await convertKMLToGeoJSON(file)

      // Flatten the GeoJSON to handle nested geometries if needed
      const localGeoJson = flatten(geoJSON)

      // Repair single coordinate line strings issue after flattening
      const repairedGeoJson1 =
        repairSingleCoordinateLineStringsIssue(localGeoJson)

      // Repair Polygon and MultiPolygon winding order after flattening
      const repairedGeoJson2 = rewind(repairedGeoJson1)

      addLayerToMap(watchLayerId, repairedGeoJson2, true)
      setHasLayerGeoJsonChanges(true)

      setValue('file', [file])
      setValue('fileName', file.name)

      // Determine file type from extension
      const fileType = file.name.toLowerCase().endsWith('.kmz') ? 'KMZ' : 'KML'
      setValue('type', fileType)
    } catch (error) {
      // Log an error for unsupported files or conversion failures
      console.error(`Error processing file ${file.name}:`, error.message)
    }
  }

  const handleOnDeleteLayerFile = (layer: ProjectMapLayer) => {
    showModal(
      <ConfirmModal
        title={localizedStrings.deleteLayer}
        confirmText={localizedStrings.delete}
        onConfirm={() => {
          setValue('file', [])
          setValue('fileName', '')
          setValue('type', null)

          deleteLayerFromMap(layer)

          hideModal()
        }}
      >
        <Text>{localizedStrings.confirmDeleteLayer}</Text>
      </ConfirmModal>,
    )
  }

  const handleOnShowHideLayer = (layer: ProjectMapLayer) => {
    setValue('visible', !watchVisible)

    toggleLayerVisibilityOnMap(layer)
  }

  const handleOnZoomToFeatures = (layer: ProjectMapLayer) => {
    zoomToLayerOnMap(layer)
  }

  const isSubmitting =
    createProjectMapLayerMutation.isLoading ||
    updateProjectMapLayerMutation.isLoading

  return (
    <Box mt={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='name'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <TextField
                label={'Layer Name'}
                error={!!fieldState.error}
                sx={{ mb: 2 }}
                fullWidth
                {...field}
              />
            )
          }}
        />

        <Controller
          name='file'
          control={control}
          render={({ field }) => {
            return (
              <>
                <AttachmentInput
                  inputProps={{
                    ...field,
                    onChange: handleOnAttachmentChange,
                  }}
                  sx={{ display: hasFile && 'none' }}
                  maxFiles={1}
                  attachmentTypes={[AttachmentTypes.KMZ, AttachmentTypes.KML]}
                />

                <LayerItem
                  sx={{ display: !hasFile && 'none' }}
                  layer={ProjectMapLayer.new({
                    id: watchLayerId,
                    name: watchFileName,
                    visible: watchVisible,
                    localGeoJSON: watchLocalGeoJson,
                  })}
                  onShowHide={handleOnShowHideLayer}
                  onDelete={handleOnDeleteLayerFile}
                  onZoomTo={handleOnZoomToFeatures}
                />
              </>
            )
          }}
        />

        <Flex marginTop={4}>
          <Button
            fullWidth
            variant='outlined'
            disabled={isSubmitting}
            onClick={onDismiss}
            sx={{ mr: 1 }}
          >
            {localizedStrings.cancel}
          </Button>
          <Button
            fullWidth
            type='submit'
            variant='contained'
            disabled={isSubmitting}
            sx={{ ml: 1 }}
          >
            {localizedStrings.save}
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
